import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Group } from '../models/Group'
import { Provider } from '../models/Provider'
import { Trainer } from '../models/Trainer'
import { User } from '../models/User'
import { AnonymousLearningItem } from '../models/AnonymousLearningItem'

/**********************************************************************
 * slices
 **********************************************************************/
const _createPerUserEntitySlice = <T,>(args: { name: string }) =>
  createSlice({
    name: args.name,
    initialState: {
      byUserId: {} as { [userUuid: string]: { [uuid: string]: T | undefined } | undefined },
    },
    reducers: {
      add(state, action: PayloadAction<{ userUuid: string; uuid: string; item: T }[]>) {
        for (const { userUuid, uuid, item } of action.payload) {
          state.byUserId[userUuid] = state.byUserId[userUuid] || {}
          state.byUserId[userUuid]![uuid] = item as any
        }
      },
    },
  })

const createEntitySlice = <T extends { uuid: string; error?: any }>(args: { name: string }) =>
  createSlice({
    name: args.name,
    initialState: {
      byId: {} as { [uuid: string]: T | undefined },
      metaById: {} as {
        [uuid: string]:
          | {
              stale?: boolean
              requested?: boolean
            }
          | undefined
      },
    },
    reducers: {
      add(state, action: PayloadAction<T[]>) {
        for (const learningItem of action.payload) {
          state.byId[learningItem.uuid] = learningItem as any
          state.metaById[learningItem.uuid] = state.metaById[learningItem.uuid] || {}
          state.metaById[learningItem.uuid]!.stale = false
          state.metaById[learningItem.uuid]!.requested = false
        }
      },
      invalidateAll(state) {
        for (const uuid of Object.keys(state.metaById)) {
          if (state.byId[uuid]?.error) {
            delete state.byId[uuid]
          }
          state.metaById[uuid] = state.metaById[uuid] || {}
          state.metaById[uuid]!.stale = true
          state.metaById[uuid]!.requested = false
        }
      },
      requested(state, action: PayloadAction<string[]>) {
        for (const uuid of action.payload) {
          state.metaById[uuid] = state.metaById[uuid] || {}
          state.metaById[uuid]!.stale = false
          state.metaById[uuid]!.requested = true
        }
      },
      update(state, action: PayloadAction<{ uuid: string } & Partial<T>>) {
        if (state.byId[action.payload.uuid]) {
          Object.assign(state.byId[action.payload.uuid] || ({} as any), action.payload)
        }
      },
    },
  })

export const usersSlice = createEntitySlice<User>({ name: 'users' })
export const groupsSlice = createEntitySlice<Group>({ name: 'groups' })
export const learningItemsSlice = createEntitySlice<AnonymousLearningItem>({ name: 'learningItems' })
export const providersSlice = createEntitySlice<Provider>({ name: 'providers' })
export const trainersSlice = createEntitySlice<Trainer>({ name: 'trainers' })
// const learningProgressSlice = createPerUserEntitySlice<any>({ name: "learningProgress" })
// const favoritesSlice = createPerUserEntitySlice<boolean>({ name: "favorites" })
// const bookingsSlice = createPerUserEntitySlice<any>({ name: "bookings" })

/**********************************************************************
 * actions
 **********************************************************************/
export const userActions = usersSlice.actions
export const groupActions = groupsSlice.actions
export const learningItemActions = learningItemsSlice.actions
export const providerActions = providersSlice.actions
export const trainerActions = trainersSlice.actions

/**********************************************************************
 * store
 **********************************************************************/
const store = configureStore({
  reducer: {
    users: usersSlice.reducer,
    groups: groupsSlice.reducer,
    learningItems: learningItemsSlice.reducer,
    providers: providersSlice.reducer,
    trainers: trainersSlice.reducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
})

export default store

export type RootState = ReturnType<typeof store.getState>
